import { SogniClient } from '@sogni-ai/sogni-client';
import { classifyJobCompletion } from './utils/job-completion.js';
import { assertRequestCurrent } from './utils/generation-request.js';
import { getGenerationErrorDetails } from './utils/generation-error-details.js';
import { isImageDeliveryUnverified } from './utils/image-delivery-status.js';
import { cancelTrackedImageProject } from './utils/image-project-cancel.js';
import { galleryVideoModels } from './utils/video-catalog.js';
import {
  createVideoSessionGuardRegistry,
  prepareVideoSdkProjectConfig
} from './utils/video-project.js';
import {
  clearSessionAccountApiCaches,
  clearSessionSocketAccountCaches,
  disconnectSessionSocket,
  installSessionSocketCacheResetHandler,
  isSameSessionIdentity,
  isSessionTransportReady,
  reconnectSessionSocket,
  revalidateSessionIdentity
} from './utils/session-revalidation.js';

/**
 * Frontend Sogni Client (base @sogni-ai/sogni-client SDK)
 *
 * One browser singleton, created with cookie authentication. Because vibe is
 * served from a *.sogni.ai subdomain, the browser attaches the shared
 * Domain=.sogni.ai HttpOnly session cookie to every api.sogni.ai request, so a
 * user logged into ANY *.sogni.ai app is authenticated here automatically via
 * `checkAuth()` — this is the cross-domain SSO mechanism used by sogni-web and
 * sogni-chat. No cookie parsing, token handoff, or custom endpoint is involved.
 *
 * This module also drives logged-in image generation (via `projects.create`)
 * and exposes the raw client/account to the billing module.
 */

export const APP_SOURCE = 'sogni-vibe';

const REST_ENDPOINT = import.meta.env.VITE_SOGNI_REST_ENDPOINT || 'https://api.sogni.ai';
const SOCKET_ENDPOINT = import.meta.env.VITE_SOGNI_SOCKET_ENDPOINT || 'https://socket.sogni.ai';
const NETWORK = import.meta.env.VITE_SOGNI_NETWORK || 'fast';
const IS_TESTNET = String(import.meta.env.VITE_SOGNI_TESTNET || 'false') === 'true';

// Reactive snapshot of the authenticated account, fed by the SDK's observable
// currentAccount entity (socket-driven). UI state modules mirror this.
export const sogniAccount = $state({
  authenticated: false,
  username: null,
  email: null,
  walletAddress: null,
  networkStatus: 'disconnected',
  balance: { spark: 0, sogni: 0, premiumSpark: 0 },
  subscription: null,
  isUnlimited: false,
  // The socket's own answer to "may this account turn the Sensitive Content
  // Filter off?" (`sensitiveContentFilterUnlocked` on the authenticated and
  // entitlement payloads). null until a payload says, or after a disconnect.
  sensitiveContentFilterUnlocked: null
});

// Carried outside the SDK account entity: the SDK does not type this field,
// so it is read from the raw socket payloads and merged into every snapshot.
let sensitiveContentFilterUnlocked = null;

// clientState is deliberately not reactive (it holds the SDK client). Bump this
// whenever a field isClientReady() depends on changes, so reactive callers
// (e.g. the Studio first-batch effect) re-run when the transport becomes ready.
const transportReadiness = $state({ version: 0 });

const clientState = {
  client: null,
  createPromise: null,
  accountUnsub: null,
  socketAuthUnsub: null,
  socketEntitlementUnsub: null,
  socketDisconnectUnsub: null,
  socketIdentity: null,
  revalidatePromise: null,
  sessionEpoch: 0,
  identityTransition: false
};

// Listeners notified whenever the account snapshot changes (balance, login, subscription…)
const accountListeners = new Set();

// Per-project generation event routing (preserves the handler contract used by sogni-generate.js)
const imageProgressHandlers = new Map();
const pendingEvents = new Map();
const projectSubscriptions = new Map();
const projectSessionEpochs = new Map();
const videoSessionGuards = createVideoSessionGuardRegistry();

const LAST_USERNAME_KEY = 'sogni-last-username';
const APP_ID_KEY = 'sogni-appId';
const SESSION_CHANGED_CODE = 'SESSION_IDENTITY_CHANGED';
const SESSION_CHANGED_ERROR = 'Sogni account changed while generation was in progress';

function clearProjectRouting() {
  imageProgressHandlers.clear();
  pendingEvents.clear();
  projectSessionEpochs.clear();
  for (const subscriptions of projectSubscriptions.values()) {
    for (const unsubscribe of subscriptions) {
      try {
        unsubscribe();
      } catch (error) {
        console.warn('Failed to remove project subscription:', error?.message || error);
      }
    }
  }
  projectSubscriptions.clear();
}

function invalidateActiveProjects(error = SESSION_CHANGED_ERROR, code = SESSION_CHANGED_CODE) {
  clientState.sessionEpoch += 1;
  const handlers = [...imageProgressHandlers.entries()];

  for (const [projectId, handler] of handlers) {
    try {
      handler({ type: 'image-failed', projectId, error, code, unverified: true });
    } catch (handlerError) {
      console.error('Project session-change handler error:', handlerError);
    }
  }

  videoSessionGuards.invalidate(error, code);
  clearProjectRouting();
}

function clearSdkProjectCache(client) {
  const projects = client?.projects?.projects;
  if (Array.isArray(projects)) projects.splice(0, projects.length);
}

function removeSdkProject(client, project) {
  const projects = client?.projects?.projects;
  if (!Array.isArray(projects)) return;
  const index = projects.indexOf(project);
  if (index >= 0) projects.splice(index, 1);
}

function quarantineAccountSession(client) {
  const wasTransitioning = clientState.identityTransition;
  clientState.identityTransition = true;
  clientState.socketIdentity = null;
  transportReadiness.version += 1;

  if (!wasTransitioning) invalidateActiveProjects();
  clearSdkProjectCache(client);
  clearSessionAccountApiCaches(client?.account);

  const account = client?.account?.currentAccount;
  if (typeof account?._clear !== 'function') {
    throw new Error('Sogni account cannot be cleared safely during identity change');
  }
  account._clear();
}

/* ------------------------------------------------------------------ */
/* App identity                                                        */
/* ------------------------------------------------------------------ */

function getOrCreateAppId() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return crypto.randomUUID();
  }
  let appId = localStorage.getItem(APP_ID_KEY);
  if (!appId) {
    appId = crypto.randomUUID();
    localStorage.setItem(APP_ID_KEY, appId);
  }
  return appId;
}

export function getLastUsername() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  return localStorage.getItem(LAST_USERNAME_KEY);
}

function rememberUsername(username) {
  if (username && typeof window !== 'undefined' && window.localStorage) {
    localStorage.setItem(LAST_USERNAME_KEY, username);
  }
}

export function clearLoggedInMark() {
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.removeItem(LAST_USERNAME_KEY);
  }
}

/**
 * Sogni redirect-login URL (fallback for non-*.sogni.ai origins or explicit
 * "sign in on app.sogni.ai" actions). SSO normally makes this unnecessary.
 */
export function getSogniAuthUrl() {
  const redirectUrl = encodeURIComponent(window.location.href);
  return `https://app.sogni.ai/authenticate/?redirect=${redirectUrl}&authMode=cookies`;
}

/* ------------------------------------------------------------------ */
/* Account snapshot / reactive state                                   */
/* ------------------------------------------------------------------ */

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function readAccountSnapshot(account) {
  const balance = account?.balance || {};
  return {
    authenticated: !!account?.isAuthenicated,
    username: account?.username || null,
    email: account?.email || null,
    walletAddress: account?.walletAddress || null,
    networkStatus: account?.networkStatus || 'disconnected',
    balance: {
      spark: toNumber(balance?.spark?.net),
      sogni: toNumber(balance?.sogni?.net),
      premiumSpark: toNumber(balance?.spark?.premiumCredit)
    },
    subscription: account?.subscription || null,
    isUnlimited: !!account?.isUnlimited,
    sensitiveContentFilterUnlocked
  };
}

function applyAccountSnapshot(account) {
  const accountSnapshot = readAccountSnapshot(account);
  if (
    !clientState.identityTransition &&
    accountSnapshot.authenticated &&
    clientState.socketIdentity &&
    !isSameSessionIdentity(accountSnapshot, clientState.socketIdentity)
  ) {
    // REST/account updates must obey the same identity invariant as socket
    // events. Quarantine before publishing a profile that belongs to a
    // different cookie session than the verified transport.
    quarantineAccountSession(clientState.client);
    void revalidateSession();
    return;
  }

  const snap = clientState.identityTransition
    ? {
        authenticated: false,
        username: null,
        email: null,
        walletAddress: null,
        networkStatus: account?.networkStatus || 'disconnected',
        balance: { spark: 0, sogni: 0, premiumSpark: 0 },
        subscription: null,
        isUnlimited: false,
        sensitiveContentFilterUnlocked: null
      }
    : accountSnapshot;
  const wasAuthenticated = sogniAccount.authenticated;
  sogniAccount.authenticated = snap.authenticated;
  sogniAccount.username = snap.username;
  sogniAccount.email = snap.email;
  sogniAccount.walletAddress = snap.walletAddress;
  sogniAccount.networkStatus = snap.networkStatus;
  sogniAccount.balance = snap.balance;
  sogniAccount.subscription = snap.subscription;
  sogniAccount.isUnlimited = snap.isUnlimited;
  sogniAccount.sensitiveContentFilterUnlocked = snap.sensitiveContentFilterUnlocked ?? null;

  // A REST 401 clears the SDK cookie auth and CurrentAccount. Preserve that
  // logout behavior while also rejecting jobs that could otherwise hang.
  if (wasAuthenticated && !snap.authenticated && !clientState.identityTransition) {
    clientState.socketIdentity = null;
    transportReadiness.version += 1;
    invalidateActiveProjects(
      'Sogni session ended while generation was in progress',
      'SESSION_ENDED'
    );
    clearSdkProjectCache(clientState.client);
  }

  if (snap.username) rememberUsername(snap.username);
  for (const listener of accountListeners) {
    try {
      listener(snap);
    } catch (err) {
      console.error('Account listener error:', err);
    }
  }
}

/**
 * Subscribe to account snapshot changes (login/logout, balance, subscription).
 * Returns an unsubscribe function.
 */
export function onAccountUpdate(listener) {
  accountListeners.add(listener);
  return () => accountListeners.delete(listener);
}

export function getAccountSnapshot() {
  return readAccountSnapshot(clientState.client?.account?.currentAccount);
}

/* ------------------------------------------------------------------ */
/* Client singleton                                                    */
/* ------------------------------------------------------------------ */

/**
 * Create (once) and return the base SogniClient singleton.
 */
export async function ensureClient() {
  if (clientState.client) return clientState.client;
  if (clientState.createPromise) return clientState.createPromise;

  clientState.createPromise = SogniClient.createInstance({
    appId: getOrCreateAppId(),
    appSource: APP_SOURCE,
    restEndpoint: REST_ENDPOINT,
    socketEndpoint: SOCKET_ENDPOINT,
    network: NETWORK,
    testnet: IS_TESTNET,
    authType: 'cookies',
    multiInstance: true,
    logLevel: 'warn'
  })
    .then((client) => {
      clientState.client = client;
      transportReadiness.version += 1;
      const socket = client.apiClient.socket;
      installSessionSocketCacheResetHandler(socket);
      clientState.socketAuthUnsub = socket.on('authenticated', (session) => {
        const socketIdentity = {
          username: session?.username || null,
          walletAddress: session?.address || null
        };
        clientState.socketIdentity = socketIdentity;
        sensitiveContentFilterUnlocked = typeof session?.sensitiveContentFilterUnlocked === 'boolean'
          ? session.sensitiveContentFilterUnlocked : null;
        transportReadiness.version += 1;

        const publishedAccount = getAccountSnapshot();
        if (
          publishedAccount.authenticated &&
          !clientState.identityTransition &&
          !isSameSessionIdentity(publishedAccount, socketIdentity)
        ) {
          // Another Vibe tab rebound the shared physical socket. Immediately
          // quarantine the old published account before any B balance or
          // entitlement can be rendered under A, then reconcile in order.
          quarantineAccountSession(client);
          void revalidateSession();
        } else {
          // Same session: publish the filter entitlement the payload carried.
          applyAccountSnapshot(client.account.currentAccount);
        }
      });
      // The entitlement push repeats the flag when a plan changes or a trial
      // hold clears. A Spark purchase only refreshes the balance, which
      // matureFilterPlanNote reads directly, so no push is needed for it.
      clientState.socketEntitlementUnsub = socket.on('subscriptionEntitlementUpdated', (data) => {
        if (typeof data?.sensitiveContentFilterUnlocked !== 'boolean') return;
        sensitiveContentFilterUnlocked = data.sensitiveContentFilterUnlocked;
        applyAccountSnapshot(client.account.currentAccount);
      });
      clientState.socketDisconnectUnsub = socket.on('disconnected', () => {
        clientState.socketIdentity = null;
        sensitiveContentFilterUnlocked = null;
        transportReadiness.version += 1;
      });
      const account = client.account.currentAccount;
      // Mirror the observable account entity into reactive state.
      clientState.accountUnsub = account.on('updated', () => applyAccountSnapshot(account));
      applyAccountSnapshot(account);
      return client;
    })
    .finally(() => {
      clientState.createPromise = null;
    });

  return clientState.createPromise;
}

export function getSogniClientInstance() {
  return clientState.client;
}

/**
 * True once the client exists and the account is authenticated.
 */
export function isClientReady() {
  void transportReadiness.version;
  return (
    !!clientState.client &&
    !!sogniAccount.authenticated &&
    isSessionTransportReady(
      getAccountSnapshot(),
      clientState.socketIdentity,
      clientState.identityTransition
    )
  );
}

/* ------------------------------------------------------------------ */
/* Session bootstrap (SSO) + credential login                          */
/* ------------------------------------------------------------------ */

/**
 * Restore a session from the shared *.sogni.ai cookie. Runs unconditionally on
 * boot — this is the SSO probe. Safe for first-time visitors (returns
 * needsLogin without error).
 */
export async function tryRestoreFromCookies() {
  try {
    await ensureClient();
    const ok = await revalidateSession();

    if (ok && isClientReady()) {
      const snapshot = getAccountSnapshot();
      const models = await getModels().catch(() => []);
      return {
        success: true,
        username: snapshot.username,
        email: snapshot.email,
        balance: snapshot.balance,
        models
      };
    }

    return { success: false, needsLogin: true };
  } catch (error) {
    console.log('Cookie session restore failed:', error?.message || error);
    return { success: false, needsLogin: true, error: error?.message };
  }
}

/**
 * Re-probe the shared cookie session (e.g. on tab refocus) to catch an account
 * switch performed in another *.sogni.ai tab. `/me` is read directly so the
 * old SDK account cannot publish the new identity while its socket is still
 * authenticated as the previous user.
 */
export async function revalidateSession() {
  if (clientState.revalidatePromise) return clientState.revalidatePromise;

  const client = clientState.client;
  if (!client) return false;

  const accountBeforeProbe = getAccountSnapshot();
  const revalidation = (async () => {
    try {
      const result = await revalidateSessionIdentity({
        currentAccount: accountBeforeProbe,
        // An unknown transport identity is intentionally treated as stale. A
        // one-time rebind is safer than assuming a shared primary socket is
        // authenticated as the account shown in this tab.
        transportAccount: clientState.socketIdentity || {},
        probeProfile: async () => {
          const response = await client.apiClient.rest.get('/v1/account/me');
          if (clientState.client !== client) {
            throw new Error('Sogni client changed during session probe');
          }
          return response?.data || response;
        },
        resetSession: () => {
          if (
            clientState.client !== client ||
            (accountBeforeProbe.authenticated &&
              !isSameSessionIdentity(accountBeforeProbe, getAccountSnapshot()))
          ) {
            throw new Error('Sogni session changed during identity revalidation');
          }

          quarantineAccountSession(client);
        },
        reconnectSession: async (profile) => {
          if (clientState.client !== client) {
            throw new Error('Sogni client changed during reconnect');
          }
          const authenticatedSession = await reconnectSessionSocket(client.apiClient.socket, {
            beforeConnect: () => clearSessionSocketAccountCaches(client.apiClient.socket)
          });
          if (
            !isSameSessionIdentity(profile, {
              username: authenticatedSession?.username,
              walletAddress: authenticatedSession?.address
            })
          ) {
            throw new Error('Sogni cookie identity changed again during socket reconnect');
          }
        },
        publishProfile: async (profile, { identityChanged }) => {
          if (clientState.client !== client) {
            throw new Error('Sogni client changed before profile publish');
          }

          // A project creation already in flight when the switch was detected
          // can settle late. Purge it again before exposing the new identity.
          if (identityChanged) clearSdkProjectCache(client);
          if (!client.apiClient.auth.isAuthenticated) {
            await client.apiClient.auth.authenticate();
          }
          client.account.currentAccount._update({
            username: profile.username,
            email: profile.currentEmail ?? profile.email,
            walletAddress: profile.walletAddress
          });
        }
      });

      return result.authenticated;
    } catch (error) {
      // RestClient clears cookie auth + CurrentAccount on a 401. Other probe or
      // reconnect failures are also fail-closed and leave the prior identity
      // unpublished.
      if (error?.status !== 401) {
        console.warn('Sogni session revalidation failed:', error?.message || error);
      }
      if (clientState.identityTransition && clientState.client === client) {
        try {
          await disconnectSessionSocket(client.apiClient.socket);
        } catch {
          // The account is still cleared below; a later probe can reconnect.
        }
        clearSessionAccountApiCaches(client.account);
        client.account.currentAccount._clear();
        clientState.socketIdentity = null;
        transportReadiness.version += 1;
      }
      return false;
    }
  })();

  let trackedRevalidation;
  trackedRevalidation = revalidation.finally(() => {
    if (clientState.revalidatePromise === trackedRevalidation) {
      clientState.revalidatePromise = null;
    }
    clientState.identityTransition = false;
    transportReadiness.version += 1;
    if (clientState.client === client) {
      applyAccountSnapshot(client.account.currentAccount);
    }
  });
  clientState.revalidatePromise = trackedRevalidation;
  return trackedRevalidation;
}

/**
 * Log in with Sogni credentials (cookie auth; session persists and propagates
 * to every *.sogni.ai app).
 */
export async function initializeSogniClient(username, password, rememberMe = true) {
  try {
    const client = await ensureClient();
    await client.account.login(username, password, rememberMe);
    if (!(await revalidateSession())) {
      throw new Error('Unable to verify the signed-in Sogni session');
    }
    applyAccountSnapshot(client.account.currentAccount);

    const snapshot = getAccountSnapshot();
    const models = await getModels().catch(() => []);
    return {
      success: true,
      username: snapshot.username || username,
      email: snapshot.email,
      balance: snapshot.balance,
      models
    };
  } catch (error) {
    console.error('Login failed:', error?.message || error);
    let errorMessage = 'Authentication failed';
    const msg = String(error?.message || '');
    const code = error?.code || error?.status;
    if (code === 105 || code === 128 || /invalid|credential|password|401/i.test(msg)) {
      errorMessage = 'Invalid username or password';
    } else if (/network|connect|fetch/i.test(msg)) {
      errorMessage = 'Unable to connect to Sogni servers';
    }
    return { success: false, error: errorMessage };
  }
}

/**
 * Create a new Sogni account (requires a Cloudflare Turnstile token).
 */
export async function signupSogniClient(
  { username, email, password, subscribe = false, turnstileToken, referralCode },
  rememberMe = true
) {
  try {
    const client = await ensureClient();
    await client.account.create(
      { username, email, password, subscribe, turnstileToken, referralCode, appSource: APP_SOURCE },
      rememberMe
    );
    if (!(await revalidateSession())) {
      throw new Error('Unable to verify the new Sogni session');
    }
    applyAccountSnapshot(client.account.currentAccount);

    const snapshot = getAccountSnapshot();
    const models = await getModels().catch(() => []);
    return {
      success: true,
      username: snapshot.username || username,
      email: snapshot.email || email,
      balance: snapshot.balance,
      models
    };
  } catch (error) {
    console.error('Signup failed:', error?.message || error);
    return { success: false, error: error?.message || 'Could not create account' };
  }
}

/**
 * Log out — clears the shared *.sogni.ai session for every app.
 */
export async function disconnectSogniClient() {
  const client = clientState.client;
  if (!client) return;
  const sessionEpoch = clientState.sessionEpoch;
  try {
    await client.account.logout();
  } catch (error) {
    console.warn('Logout request failed (continuing):', error?.message || error);
  }
  // Reset local reactive state; keep the client instance for a fresh login.
  applyAccountSnapshot(client.account.currentAccount);
  if (clientState.sessionEpoch === sessionEpoch) {
    invalidateActiveProjects('Signed out while generation was in progress', 'SESSION_ENDED');
  }
  clearSdkProjectCache(client);
}

/* ------------------------------------------------------------------ */
/* Balance + models                                                    */
/* ------------------------------------------------------------------ */

export async function getBalance() {
  const client = clientState.client;
  if (!client || !isClientReady()) throw new Error('Sogni client not connected');
  const balance = await client.account.refreshBalance();
  return {
    spark: toNumber(balance?.spark?.net),
    sogni: toNumber(balance?.sogni?.net),
    premiumSpark: toNumber(balance?.spark?.premiumCredit)
  };
}

/**
 * Fetch account profile (includes `emailVerified`).
 */
export async function fetchMe() {
  const client = clientState.client;
  if (!client || !isClientReady()) return null;
  try {
    return await client.account.me();
  } catch (error) {
    console.error('Failed to fetch account profile:', error?.message || error);
    return null;
  }
}

/**
 * Resend the account verification email.
 */
export async function resendVerificationEmail() {
  const client = clientState.client;
  if (!client || !isClientReady()) throw new Error('Sogni client not connected');
  await client.apiClient.rest.post('/v1/account/email/resend', {});
}

function mapModels(models) {
  return (Array.isArray(models) ? models : [])
    .filter(Boolean)
    .map((m) => ({
      id: m.id,
      name: m.name || m.id,
      workerCount: m.workerCount || 0,
      media: m.media || 'image'
    }));
}

export async function getModels(network) {
  return getModelsByMedia('image', network);
}

async function getModelsByMedia(media, network) {
  const client = clientState.client;
  if (!client) throw new Error('Sogni client not connected');
  if (network) {
    return mapModels(await client.projects.getAvailableModels(network)).filter((m) => m.media === media);
  }
  let models = client.projects.availableModels;
  if (!models || models.length === 0) {
    models = await client.projects.waitForModels(15000).catch(() => []);
  }
  return mapModels(models).filter((m) => m.media === media);
}

export async function getVideoModels() {
  const client = clientState.client;
  if (!client) throw new Error('Sogni client not connected');
  return galleryVideoModels(await client.apiClient.rest.get('/v1/model-catalog?mediaType=video&include=parameters'));
}

export async function getModel(modelId) {
  const models = await getModels();
  const model = models.find((m) => m.id === modelId);
  if (!model) throw new Error(`Model ${modelId} not available`);
  return model;
}

export async function getVideoModel(modelId, network = 'fast') {
  const models = await getModelsByMedia('video', network);
  const model = models.find((m) => m.id === modelId);
  if (!model) throw new Error(`Video model ${modelId} not available`);
  return model;
}

export async function getMostPopularModel() {
  const models = await getModels();
  if (!models.length) throw new Error('No models available');
  return models.reduce((best, m) => (m.workerCount > (best?.workerCount || 0) ? m : best), models[0]);
}

/* ------------------------------------------------------------------ */
/* Image generation (logged-in users generate client-side)            */
/* ------------------------------------------------------------------ */

function mapProjectConfig(config) {
  // Translate the legacy wrapper config to base-SDK ImageProjectParams.
  const {
    numberOfImages,
    numberOfMedia,
    batchSize, // unused by base SDK
    waitForCompletion, // handled via events, not a param
    type, // always image here
    billingMode,
    ...rest
  } = config;

  return {
    ...rest,
    type: 'image',
    numberOfMedia: numberOfMedia ?? numberOfImages ?? 1,
    // 'auto' draws on an Unlimited subscription when available, else spends tokens.
    billingMode: billingMode || 'auto',
    appSource: APP_SOURCE
  };
}

function dispatchProjectEvent(projectId, event) {
  const handler = imageProgressHandlers.get(projectId);
  if (handler) {
    handler(event);
    return;
  }
  if (!pendingEvents.has(projectId)) pendingEvents.set(projectId, []);
  pendingEvents.get(projectId).push(event);
}

function trackProjectEvents(project) {
  const pid = project.id;
  const unsubs = [];
  let lastPreviewUrl = null;
  let terminalDispatched = false;

  const dispatchTerminalEvent = (event) => {
    if (terminalDispatched) return;
    terminalDispatched = true;
    dispatchProjectEvent(pid, event);
  };
  const completeJob = (job) => {
    const completion = classifyJobCompletion(job, {
      allowNSFWResult: project?.params?.disableNSFWFilter === true
    });
    dispatchTerminalEvent(completion.success ? {
      type: 'image-completed', projectId: pid, jobId: job?.id || null,
      workerName: job?.workerName || null, seed: job?.seed ?? null,
      imageUrl: completion.resultUrl, sensitive: completion.sensitive === true
    } : {
      type: 'image-failed', projectId: pid, error: completion.error,
      code: completion.code, unverified: isImageDeliveryUnverified(completion, pid),
      isNSFWFiltered: completion.isNSFWFiltered
    });
  };
  const failProject = (error) => {
    const details = getGenerationErrorDetails(error);
    dispatchTerminalEvent({ ...details, type: 'image-failed', projectId: pid,
      unverified: isImageDeliveryUnverified(error, pid),
      error: details.message || 'Image generation failed' });
  };

  projectSessionEpochs.set(pid, clientState.sessionEpoch);

  unsubs.push(
    project.on('progress', (percentage) => {
      dispatchProjectEvent(pid, { type: 'progress', projectId: pid, percentage });
    })
  );

  // Preview frames arrive via the job's `updated` event (job.previewUrl). Emit
  // them so users see progressive previews. numberOfPreviews is billed, so
  // surfacing them matters.
  unsubs.push(
    project.on('updated', () => {
      if (project.status === 'canceled') {
        dispatchProjectEvent(pid, { type: 'image-canceled', projectId: pid });
        return;
      }
      if (!project.finished) dispatchProjectEvent(pid, { type: 'project-status', projectId: pid,
        status: project.status, percentage: project.progress, queuePosition: project.queuePosition,
        estimatedStartAt: project.estimatedStartAt, waitingReason: project.waitingReason });
      const job = project.jobs && project.jobs[0];
      const url = job?.previewUrl;
      if (url && url !== lastPreviewUrl) {
        lastPreviewUrl = url;
        dispatchProjectEvent(pid, {
          type: 'image-preview',
          projectId: pid,
          previewUrl: url,
          percentage: project.progress,
          step: job.step,
          stepCount: job.stepCount
        });
      }
    })
  );

  unsubs.push(project.on('jobCompleted', completeJob));
  unsubs.push(project.on('jobFailed', (job) => failProject(job?.error)));
  unsubs.push(project.on('failed', failProject));

  projectSubscriptions.set(pid, unsubs);
  // SDK create can reuse an entity that sync completed before its browser ACK.
  // Subscriptions do not replay: consume that same entity without another create.
  if (project.status === 'completed') {
    const job = project.jobs?.[0];
    const epoch = clientState.sessionEpoch;
    const replay = () => {
      if (projectSessionEpochs.get(pid) === epoch) completeJob(job);
    };
    // getResultUrl uses an existing result or mints this known job's signed URL.
    // A missing job or failed download lookup is still an unverified delivery.
    Promise.resolve().then(() => job?.getResultUrl()).then(replay, replay);
  } else if (project.status === 'failed') {
    failProject(project.error || project.jobs?.[0]?.error);
  } else if (project.status === 'canceled') {
    completeJob({ status: 'canceled' });
  }
}

/**
 * Create a project only while the authenticated REST and socket identities
 * agree. The continuation runs synchronously after the final epoch check so
 * project tracking is installed before a session invalidation can interleave.
 */
async function createAuthenticatedProject(config, continueWithProject, request) {
  let revalidation = clientState.revalidatePromise;
  if (
    !revalidation &&
    (!clientState.socketIdentity ||
      !isSameSessionIdentity(getAccountSnapshot(), clientState.socketIdentity))
  ) {
    // A different Vibe tab can rebind the shared physical socket before this
    // tab's focus handler runs. Never create against an unknown/mismatched
    // transport identity; reconcile it first.
    revalidation = revalidateSession();
  }
  if (revalidation && !(await revalidation)) {
    const error = new Error('Sogni session could not be revalidated');
    error.code = SESSION_CHANGED_CODE;
    throw error;
  }

  const client = clientState.client;
  if (!client || !isClientReady() || clientState.identityTransition) {
    throw new Error('Sogni client not connected');
  }

  if (request) {
    const account = getAccountSnapshot();
    // Settings may be edited for a future batch while these frozen inputs run.
    // The reviewed owner and entitlement must still match at SDK admission.
    assertRequestCurrent(request, { accountId: account.walletAddress?.toLowerCase(),
      sourceFingerprint: request.sourceFingerprint, subscription: account.subscription });
  }
  const sessionEpoch = clientState.sessionEpoch;
  const sdkSessionVersion = client.apiClient.auth.sessionVersion;
  const sessionIsCurrent = () => clientState.client === client
    && clientState.sessionEpoch === sessionEpoch
    && client.apiClient.auth.sessionVersion === sdkSessionVersion
    && !clientState.identityTransition && isClientReady();
  let project;
  try {
    project = await client.projects.create(config);
  } catch (failure) {
    if (sessionIsCurrent()) throw failure;
    // The SDK can send jobRequest, then reject its session assertion and
    // dispose the local project without returning its ID. Delivery remains
    // uncertain; the captured attribution supports exact recovery.
    const { message, ...details } = getGenerationErrorDetails(failure);
    const error = Object.assign(new Error(SESSION_CHANGED_ERROR), details, {
      code: SESSION_CHANGED_CODE, request, unverified: true, cause: failure
    });
    throw error;
  }
  if (!sessionIsCurrent()) {
    removeSdkProject(client, project);
    try {
      await project.cancel?.();
    } catch {
      // Identity safety still fails closed even if the remote cancel races.
    }
    const error = new Error(SESSION_CHANGED_ERROR);
    error.code = SESSION_CHANGED_CODE;
    error.request = request;
    error.projectId = project.id;
    error.unverified = true; // Best-effort cancellation has no terminal receipt.
    throw error;
  }

  return continueWithProject(project);
}

/**
 * Create an image project. Returns `{ project }` (with `project.id`); results
 * arrive via the handler registered through `registerProjectHandler`.
 */
export async function createProject(config, { request } = {}) {
  return createAuthenticatedProject(mapProjectConfig(config), (project) => {
    trackProjectEvents(project);
    return { project };
  }, request);
}

export async function cancelImageProject(projectId, request) {
  const snapshot = getAccountSnapshot();
  const accountId = snapshot.authenticated ? snapshot.walletAddress?.toLowerCase() : null;
  if (!isClientReady() || accountId !== request.accountId) {
    return { confirmed: false, state: 'unconfirmed', projectId,
      message: 'Sign in to the account that submitted this image to cancel it.' };
  }
  const client = clientState.client;
  const sessionEpoch = clientState.sessionEpoch;
  const sessionVersion = client.apiClient.auth.sessionVersion;
  const assertCurrent = () => {
    const current = getAccountSnapshot();
    if (clientState.client !== client || clientState.sessionEpoch !== sessionEpoch
      || client.apiClient.auth.sessionVersion !== sessionVersion || !isClientReady()
      || !current.authenticated || current.walletAddress?.toLowerCase() !== request.accountId) {
      throw Object.assign(new Error('Your account changed before image cancellation could be confirmed.'),
        { code: 'SESSION_IDENTITY_CHANGED' });
    }
  };
  const result = await cancelTrackedImageProject(client.projects, projectId,
    { socket: client.apiClient.socket, assertCurrent });
  if (result.confirmed) dispatchProjectEvent(projectId, { type: 'image-canceled', projectId });
  return result;
}

/**
 * Create a direct user-owned video project without attaching image event
 * routing. The caller owns completion, cancellation, and result handling.
 */
export async function createVideoProject(config, { request } = {}) {
  return createAuthenticatedProject(
    prepareVideoSdkProjectConfig(config, { appSource: APP_SOURCE }),
    (project) => {
      const sessionGuard = videoSessionGuards.track(project);
      return {
        project,
        sessionInvalidated: sessionGuard.invalidated,
        releaseSessionGuard: sessionGuard.release
      };
    },
    request
  );
}

/** Dedicated image utilities retain the native source and their model-specific fields. */
export async function createUtilityImageProject(config, { request } = {}) {
  return createAuthenticatedProject({ ...config, type: 'image', appSource: APP_SOURCE }, (project) => {
    const guard = videoSessionGuards.track(project);
    return { project, sessionInvalidated: guard.invalidated, releaseSessionGuard: guard.release };
  }, request);
}

export function registerProjectHandler(projectId, handler) {
  if (
    projectSessionEpochs.get(projectId) !== clientState.sessionEpoch ||
    clientState.identityTransition
  ) {
    handler({
      type: 'image-failed',
      projectId,
      error: SESSION_CHANGED_ERROR,
      code: SESSION_CHANGED_CODE,
      unverified: true
    });
    return;
  }

  imageProgressHandlers.set(projectId, handler);
  const pending = pendingEvents.get(projectId);
  if (pending && pending.length > 0) {
    for (const event of pending) handler(event);
    pendingEvents.delete(projectId);
  }
}

export function unregisterProjectHandler(projectId) {
  imageProgressHandlers.delete(projectId);
  pendingEvents.delete(projectId);
  projectSessionEpochs.delete(projectId);
  const subs = projectSubscriptions.get(projectId);
  if (subs) {
    subs.forEach((u) => u());
    projectSubscriptions.delete(projectId);
  }
}
